import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useDashboard } from '../../contexts/DashboardContext';
import { loadPopout, openPopout, savePopout } from '../../utils/popout';
import { useUpgradeAction } from '../../hooks/useUpgradeAction';
import { isQfConnected, onQfConnected, onQfConnectionChange, qfSubscribe } from '../../services/qfClient';
import { chartBarsService } from '../../services/chartBarsService';
import { getNqBars, getNqForming, type NqMinute } from '../../services/nqScalperService';
import type { ToolWindow } from '../../types/dashboard';
import { underlyingPriceTicker } from '../../utils/tickerDisplay';
import { minuteKey, sessionWindow, type ScalperBar } from '../nq-scalper/signals';
import { TickerLinkButton } from '../TickerLinkButton';
import { tickerNotInPlanFrom } from '../tickerNotInPlan';
import { LiveIndicator, WidgetHeader } from '../WidgetHeader';
import {
  chartIntervalFromCode,
  pushZerodayMinute,
  pushZerodayNqMinutes,
  setZerodayForming,
  setZerodayNqPollDown,
  subscribeZerodayNqStatus,
} from './zerodayIndicator';
import { adoptHistory, mergeLiveMinutes, sourceFor, type CandleBar } from './barSource';
import { isRth } from './shareMinutes';
import { OpenAlgoCandleChart } from './OpenAlgoCandleChart';
import {
  defaultSessionShade,
  emptySavedChart,
  intervalAllowsExtended,
  readSavedChart,
  sessionFilterFor,
  sharedZoomKey,
  shouldSeedIndicator,
  type SavedChart,
  type SessionShade,
  type ZoomWindow,
} from './savedChart';

interface CandleChartWidgetProps {
  windowId: string;
  onRemove: () => void;
}

const DEFAULT_TICKER = 'SPX';
const SAVE_MS = 400;
const NO_DRAWINGS: Record<string, unknown> = {};
const NO_ZOOM: Record<string, ZoomWindow> = {};

function readTicker(window: ToolWindow | undefined): string {
  const fromWindow = window?.ticker?.trim().toUpperCase();
  if (fromWindow) return fromWindow;
  const raw: unknown = window?.config?.ticker;
  if (typeof raw === 'string' && raw.trim()) return raw.trim().toUpperCase();
  return DEFAULT_TICKER;
}

function livePrint(raw: unknown): CandleBar | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Record<string, unknown>;
  if (typeof record.minute !== 'string') return null;
  const { open, high, low, close } = record;
  if (
    typeof open !== 'number'
    || typeof high !== 'number'
    || typeof low !== 'number'
    || typeof close !== 'number'
  ) {
    return null;
  }
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(record.minute) ? record.minute : `${record.minute}Z`;
  const time = minuteKey(Date.parse(iso));
  if (!Number.isFinite(time)) return null;
  const volume = typeof record.volume === 'number' && record.volume > 0 ? record.volume : 0;
  return { time, open, high, low, close, volume };
}

function nqMinuteToBar(row: NqMinute): CandleBar | null {
  const time = minuteKey(Date.parse(row.ts));
  if (!Number.isFinite(time)) return null;
  return {
    time,
    open: row.open,
    high: row.high,
    low: row.low,
    close: row.close,
    volume: row.volume ?? 0,
  };
}

function formingBar(row: NqMinute | null): ScalperBar | null {
  if (!row) return null;
  const minute = minuteKey(Date.parse(row.ts));
  if (!Number.isFinite(minute)) return null;
  return { minute, close: row.close };
}

const POPOUT_TOOL = 'candle-chart';

const headerButton = 'text-gray-400 hover:text-white text-sm px-2 py-0.5 rounded transition-colors';

function PopOutIcon(): ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M7 1.5h3.5V5M10.5 1.5 5.5 6.5M9 7.5v3H1.5V3h3" />
    </svg>
  );
}

function CloseButton({ onRemove, title }: { onRemove: () => void; title: string }): ReactElement {
  return (
    <button
      type="button"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={onRemove}
      className={`${headerButton} hover:bg-bear-red/20`}
      title={title}
    >
      x
    </button>
  );
}

interface CandleChartViewProps {
  windowId: string;
  onRemove: () => void;
  /** The settings this chart starts from. */
  saved: ToolWindow | undefined;
  /** Merge into this chart's saved config. */
  saveConfig: (config: Record<string, unknown>) => void;
  /** Ticker typed into the chart, for the dashboard's ticker links. */
  linkTicker: (ticker: string) => void;
  /** Docked only. Opens a pop-out that starts as a copy of this chart. */
  onPopOut?: (start: ToolWindow) => void;
}

/** The chart on a dashboard. */
export function CandleChartWidget({ windowId, onRemove }: CandleChartWidgetProps): ReactElement {
  const { currentDashboard, updateWindowConfig, syncTicker } = useDashboard();
  const saved = currentDashboard?.windows.find((item) => item.id === windowId);
  const saveConfig = useCallback((config: Record<string, unknown>): void => {
    updateWindowConfig(windowId, config);
  }, [updateWindowConfig, windowId]);
  const linkTicker = useCallback((ticker: string): void => {
    syncTicker(ticker, [windowId]);
  }, [syncTicker, windowId]);
  const onPopOut = useCallback((start: ToolWindow): void => {
    openPopout(POPOUT_TOOL, windowId, start);
  }, [windowId]);
  return (
    <CandleChartView
      windowId={windowId}
      onRemove={onRemove}
      saved={saved}
      saveConfig={saveConfig}
      linkTicker={linkTicker}
      onPopOut={onPopOut}
    />
  );
}

const NO_LINK = (): void => {};

/**
 * The chart in its own window. It starts as a copy of the docked chart and
 * saves to its own entry, so its edits never reach the dashboard.
 */
export function CandleChartPopout({ popoutId }: { popoutId: string }): ReactElement {
  const [start, setStart] = useState<ToolWindow | undefined>(undefined);
  const startRef = useRef<ToolWindow | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    loadPopout(popoutId)
      .catch((): null => null)
      .then((loaded) => {
        if (!alive) return;
        const first: ToolWindow = loaded ?? { id: popoutId, type: 'nq-scalper' };
        startRef.current = first;
        setStart(first);
      });
    return () => {
      alive = false;
    };
  }, [popoutId]);

  const saveConfig = useCallback((config: Record<string, unknown>): void => {
    const current = startRef.current;
    if (!current) return;
    const ticker = typeof config.ticker === 'string' ? config.ticker : current.ticker;
    const next: ToolWindow = { ...current, ticker, config: { ...current.config, ...config } };
    // Saved, not re-rendered: the chart reads its settings once and owns them after that.
    startRef.current = next;
    savePopout(popoutId, next).catch((error: unknown) => {
      console.error('Failed to save pop-out chart:', error);
    });
  }, [popoutId]);

  if (start === undefined) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-gray-500">Loading</div>
    );
  }
  return (
    <CandleChartView
      windowId={popoutId}
      onRemove={() => window.close()}
      saved={start}
      saveConfig={saveConfig}
      linkTicker={NO_LINK}
    />
  );
}

function CandleChartView({
  windowId,
  onRemove,
  saved,
  saveConfig,
  linkTicker,
  onPopOut,
}: CandleChartViewProps): ReactElement {
  const inPopout = onPopOut === undefined;
  const savedTicker = readTicker(saved);
  const [boot] = useState<SavedChart | undefined>(() => readSavedChart(saved?.config?.chart));
  const chartRef = useRef<SavedChart>(boot ?? emptySavedChart());
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [ticker, setTicker] = useState(savedTicker);
  const [trackedTicker, setTrackedTicker] = useState(savedTicker);
  if (savedTicker !== trackedTicker) {
    setTrackedTicker(savedTicker);
    setTicker(savedTicker);
  }
  const [intervalCode, setIntervalCode] = useState(boot?.interval ?? '1m');
  const [chartType, setChartType] = useState(boot?.chartType ?? 'candlestick');
  const [extendedHours, setExtendedHours] = useState(boot?.extendedHours === true);
  const [crosshair, setCrosshair] = useState(boot?.crosshair !== false);
  const [sessionShade, setSessionShade] = useState<SessionShade>(
    boot?.sessionShade ?? defaultSessionShade(),
  );
  const sessionFilter = sessionFilterFor(extendedHours, intervalCode);
  const liveKey = `${ticker}|${intervalCode}|${sessionFilter}`;
  const [series, setSeries] = useState<readonly CandleBar[]>([]);
  const [seriesKey, setSeriesKey] = useState(liveKey);
  if (liveKey !== seriesKey) {
    setSeriesKey(liveKey);
    setSeries([]);
  }
  const bars = liveKey === seriesKey ? series : [];
  const seriesRef = useRef<readonly CandleBar[]>([]);
  const serverSeen = useRef<readonly CandleBar[] | undefined>(undefined);
  const appliedKey = useRef(liveKey);
  const [chromeSlot, setChromeSlot] = useState<HTMLElement | null>(null);
  const [nqDown, setNqDown] = useState(false);
  const [qfConnected, setQfConnected] = useState(() => isQfConnected());
  const previousForming = useRef<number | null>(null);

  const writeChart = useCallback((patch: Partial<SavedChart>): void => {
    chartRef.current = { ...chartRef.current, ...patch };
    if (saveTimer.current != null) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      saveConfig({ chart: { ...chartRef.current } });
    }, SAVE_MS);
  }, [saveConfig]);

  useEffect(() => () => {
    if (saveTimer.current == null) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = null;
    saveConfig({ chart: { ...chartRef.current } });
  }, [saveConfig]);

  // Closing a pop-out window does not unmount React. Save the pending edit before the tab goes.
  useEffect(() => {
    if (!inPopout) return undefined;
    const flush = (): void => {
      if (saveTimer.current == null) return;
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      saveConfig({ chart: { ...chartRef.current } });
    };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [inPopout, saveConfig]);

  useEffect(() => onQfConnectionChange(setQfConnected), []);
  useEffect(() => subscribeZerodayNqStatus(setNqDown), []);

  const interval = useMemo(() => chartIntervalFromCode(intervalCode), [intervalCode]);
  const sessionEnabled = intervalAllowsExtended(intervalCode);
  const barsQuery = useQuery({
    queryKey: ['candle-chart', ticker, intervalCode, sessionFilter],
    queryFn: () => chartBarsService.getBars({
      symbol: ticker,
      interval,
      sessionFilter,
    }),
    staleTime: 15_000,
    refetchInterval: sourceFor(ticker) === 'cme' ? 30_000 : false,
    // A slept tab misses prints without a clean socket drop. Spot history is
    // the base the live tail extends, so bring it forward when the tab returns.
    refetchOnWindowFocus: sourceFor(ticker) === 'spot',
    refetchOnReconnect: sourceFor(ticker) === 'spot',
    retry: 1,
  });
  const nqQuery = useQuery({
    queryKey: ['candle-nq-bars'],
    queryFn: () => {
      const session = sessionWindow(Date.now());
      return getNqBars(new Date(session.startMs).toISOString(), new Date(session.endMs).toISOString());
    },
    refetchInterval: 30_000,
    retry: 1,
  });
  const formingQuery = useQuery({
    queryKey: ['candle-nq-forming'],
    queryFn: getNqForming,
    refetchInterval: 1000,
    retry: false,
  });
  const { refetch: refetchNq } = nqQuery;

  const formingMinute = formingQuery.data ? minuteKey(Date.parse(formingQuery.data.ts)) : null;
  useEffect(() => {
    if (formingMinute === null) return;
    const previous = previousForming.current;
    previousForming.current = formingMinute;
    if (previous !== null && previous !== formingMinute) void refetchNq();
  }, [formingMinute, refetchNq]);

  useEffect(() => {
    setZerodayNqPollDown(nqQuery.isError);
  }, [nqQuery.isError]);

  useEffect(() => {
    if (!nqQuery.data) return;
    const bars = nqQuery.data.flatMap((row) => {
      const bar = nqMinuteToBar(row);
      return bar ? [bar] : [];
    });
    pushZerodayNqMinutes(bars);
  }, [nqQuery.data]);

  useEffect(() => {
    setZerodayForming(formingBar(formingQuery.data ?? null));
  }, [formingQuery.data]);

  // History is the base. Live prints merge into seriesRef and stay there.
  // A capped replay onto a frozen snapshot drops minutes the socket already
  // delivered. The ref is read from the flush, never from render, so a
  // parent render cannot put a stale snapshot back.
  const serverBars = barsQuery.data;
  const intervalRef = useRef(interval);
  const sessionRef = useRef(sessionFilter);
  const refetchRef = useRef(barsQuery.refetch);

  useLayoutEffect(() => {
    intervalRef.current = interval;
    sessionRef.current = sessionFilter;
    refetchRef.current = barsQuery.refetch;
    const keyChanged = appliedKey.current !== liveKey;
    if (keyChanged) {
      appliedKey.current = liveKey;
      seriesRef.current = [];
      serverSeen.current = undefined;
    }
    if (serverBars && serverBars !== serverSeen.current) {
      serverSeen.current = serverBars;
      seriesRef.current = adoptHistory(serverBars, seriesRef.current, interval, sessionFilter);
      setSeries(seriesRef.current);
      return;
    }
    if (keyChanged) setSeries([]);
  }, [barsQuery.refetch, interval, liveKey, serverBars, sessionFilter]);

  useEffect(() => {
    if (sourceFor(ticker) !== 'spot') return undefined;
    return onQfConnected(() => {
      void refetchRef.current();
    });
  }, [ticker]);

  useEffect(() => {
    if (sourceFor(ticker) !== 'spot') return undefined;
    const root = underlyingPriceTicker(ticker);
    const queued = new Map<number, CandleBar>();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let alive = true;
    const flush = (): void => {
      timer = null;
      if (!alive) return;
      const batch = [...queued.values()];
      queued.clear();
      if (batch.length === 0) return;
      const applied = mergeLiveMinutes(
        seriesRef.current,
        batch,
        intervalRef.current,
        sessionRef.current,
      );
      seriesRef.current = applied.bars;
      setSeries(applied.bars);
      if (applied.skipped) void refetchRef.current();
      for (const print of batch) {
        if (isRth(print.time)) pushZerodayMinute(ticker, print);
      }
    };
    const stop = qfSubscribe(`ohlc:${root}:1m`, (raw) => {
      const print = livePrint(raw);
      if (!print) return;
      queued.set(print.time, print);
      if (timer != null) return;
      timer = setTimeout(flush, 150);
    });
    return () => {
      alive = false;
      if (timer != null) clearTimeout(timer);
      queued.clear();
      stop();
    };
  }, [ticker]);

  const applyTicker = useCallback((raw: string): void => {
    const next = raw.trim().toUpperCase();
    if (!next || next === ticker) return;
    setTicker(next);
    saveConfig({ ticker: next });
    linkTicker(next);
  }, [linkTicker, saveConfig, ticker]);

  const onChrome = useCallback((slot: HTMLElement | null): void => {
    setChromeSlot(slot);
  }, []);

  const onInterval = useCallback((code: string): void => {
    setIntervalCode(code);
    writeChart({ interval: code });
  }, [writeChart]);

  const onChartType = useCallback((id: string): void => {
    setChartType(id);
    writeChart({ chartType: id });
  }, [writeChart]);

  const onExtendedHours = useCallback((extended: boolean): void => {
    setExtendedHours(extended);
    writeChart({ extendedHours: extended });
  }, [writeChart]);

  const onCrosshair = useCallback((on: boolean): void => {
    setCrosshair(on);
    writeChart({ crosshair: on });
  }, [writeChart]);

  const onState = useCallback((openAlgo: unknown): void => {
    writeChart({ openAlgo });
  }, [writeChart]);

  const onDrawings = useCallback((drawings: Record<string, unknown>): void => {
    writeChart({ drawings });
  }, [writeChart]);

  const onView = useCallback((code: string, window: ZoomWindow): void => {
    const key = sharedZoomKey(code);
    writeChart({ zoom: { ...chartRef.current.zoom, [key]: window } });
  }, [writeChart]);

  const onSessionShade = useCallback((shade: SessionShade): void => {
    setSessionShade(shade);
    writeChart({ sessionShade: shade });
  }, [writeChart]);

  const blocked = barsQuery.isError ? tickerNotInPlanFrom(barsQuery.error, ticker) : null;
  const { onUpgrade, isRedirecting } = useUpgradeAction(blocked !== null, blocked?.upgrade_url);
  const live = sourceFor(ticker) === 'cme'
    ? Boolean(barsQuery.data && !barsQuery.isError)
    : qfConnected;

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      <WidgetHeader title="Candle Chart" badge="new" indicator={<LiveIndicator isConnected={live} />}>
        {!inPopout && (
          <button
            type="button"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={() => onPopOut?.({
              ...(saved ?? { id: windowId, type: 'nq-scalper' }),
              ticker,
              config: { ...saved?.config, ticker, chart: { ...chartRef.current } },
            })}
            className={`${headerButton} hover:bg-dark-surface-light inline-flex items-center`}
            title="Open in a new window"
            aria-label="Open in a new window"
          >
            <PopOutIcon />
          </button>
        )}
        <CloseButton onRemove={onRemove} title={inPopout ? 'Close window' : 'Close widget'} />
      </WidgetHeader>
      <div className="flex-1 min-h-0 relative bg-[#0A0A0A]">
        {nqDown && !barsQuery.isError && (
          <div className="px-3 py-1 text-xs text-gray-500 border-b border-dark-border">
            NQ signals are unavailable. Candles still update.
          </div>
        )}
        {barsQuery.isError && blocked && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-dark-bg/95 gap-3 px-6 text-center">
            <h3 className="text-base font-semibold text-gray-100">{blocked.message}</h3>
            {blocked.allowed_tickers.length > 0 && (
              <p className="text-xs text-gray-400">
                Your plan includes: {blocked.allowed_tickers.join(', ')}
              </p>
            )}
            <button
              type="button"
              onClick={() => { void onUpgrade(); }}
              disabled={isRedirecting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-bull-blue hover:bg-bull-blue/90 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {isRedirecting ? 'Redirecting...' : 'Upgrade to FZN Pro'}
            </button>
          </div>
        )}
        {barsQuery.isError && !blocked && (
          <div className="absolute inset-0 z-20 flex items-center justify-center text-sm text-gray-500">
            No data for {ticker}
          </div>
        )}
        {barsQuery.isLoading && bars.length === 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center text-sm text-gray-500">
            Loading
          </div>
        )}
        {!barsQuery.isError && (
          <OpenAlgoCandleChart
            bars={bars}
            seriesKey={`${ticker}|${intervalCode}|${sessionFilter}`}
            symbol={ticker}
            intervalCode={intervalCode}
            chartType={chartType}
            extendedHours={extendedHours}
            sessionEnabled={sessionEnabled}
            crosshair={crosshair}
            seedIndicator={shouldSeedIndicator(boot)}
            savedState={boot?.openAlgo}
            drawings={boot?.drawings ?? NO_DRAWINGS}
            zoom={boot?.zoom ?? NO_ZOOM}
            sessionShade={sessionShade}
            onSymbol={applyTicker}
            onInterval={onInterval}
            onChartType={onChartType}
            onChrome={onChrome}
            onExtendedHours={onExtendedHours}
            onCrosshair={onCrosshair}
            onState={onState}
            onDrawings={onDrawings}
            onView={onView}
            onSessionShade={onSessionShade}
          />
        )}
        {chromeSlot && !inPopout && createPortal(
          <TickerLinkButton
            windowId={windowId}
            currentTicker={ticker}
            onTickerChange={applyTicker}
          />,
          chromeSlot,
        )}
      </div>
    </div>
  );
}
